import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { useListingsStore } from '../stores/listings';

// Citta con immagini hero
const HERO_CITIES = [
  { name: 'Milano', image: '/images/cities/milano.webp' },
  { name: 'Roma', image: '/images/cities/roma.webp' },
  { name: 'Napoli', image: '/images/cities/napoli.webp' },
  { name: 'Torino', image: '/images/cities/torino.webp' },
  { name: 'Bologna', image: '/images/cities/bologna.webp' },
  { name: 'Firenze', image: '/images/cities/firenze.webp' },
];

// Citta piu cercate con dati realistici
const POPULAR_CITIES = [
  { name: 'Milano', count: '12.5k' },
  { name: 'Roma', count: '10.2k' },
  { name: 'Napoli', count: '5.8k' },
  { name: 'Torino', count: '4.3k' },
  { name: 'Bologna', count: '3.9k' },
  { name: 'Firenze', count: '3.2k' },
];

// Suggerimenti autocomplete
const CITY_SUGGESTIONS = [
  'Milano', 'Roma', 'Napoli', 'Torino', 'Bologna', 'Firenze',
  'Genova', 'Palermo', 'Bari', 'Catania', 'Venezia', 'Verona',
  'Padova', 'Trieste', 'Brescia', 'Parma', 'Modena', 'Reggio Emilia',
];

export default function Home() {
  const navigate = useNavigate();
  const { setFilters, fetchListings } = useListingsStore();
  const [city, setCity] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);
  const [currentHeroIndex, setCurrentHeroIndex] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState<boolean[]>(new Array(HERO_CITIES.length).fill(false));
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Preload hero images
  useEffect(() => {
    HERO_CITIES.forEach((city, index) => {
      const img = new Image();
      img.onload = () => {
        setImagesLoaded(prev => {
          const next = [...prev];
          next[index] = true;
          return next;
        });
      };
      img.src = city.image;
    });
  }, []);

  // Auto-rotate hero background every 6 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHeroIndex(prev => (prev + 1) % HERO_CITIES.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  // Filtra suggerimenti in base all'input
  useEffect(() => {
    if (city.length > 0) {
      const filtered = CITY_SUGGESTIONS.filter(c =>
        c.toLowerCase().startsWith(city.toLowerCase())
      ).slice(0, 5);
      setFilteredSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setShowSuggestions(false);
    }
  }, [city]);

  // Chiudi suggerimenti quando si clicca fuori
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (searchCity?: string) => {
    const cityToSearch = searchCity || city;
    setFilters({
      city: cityToSearch || undefined,
    });
    fetchListings();
    navigate('/listings');
  };

  const handleSuggestionClick = (suggestion: string) => {
    setCity(suggestion);
    setShowSuggestions(false);
    handleSearch(suggestion);
  };

  const handleCityClick = (cityName: string) => {
    setFilters({ city: cityName });
    fetchListings();
    navigate('/listings');
  };

  return (
    <div className="flex flex-col min-h-[calc(100vh-4rem)]">
      {/* Hero Section con immagini citta rotanti */}
      <section className="relative flex-1 flex items-center justify-center min-h-[480px] md:min-h-[540px] overflow-hidden">
        {/* Background images con crossfade */}
        {HERO_CITIES.map((heroCity, index) => (
          <div
            key={heroCity.name}
            className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ease-in-out"
            style={{
              backgroundImage: imagesLoaded[index] ? `url(${heroCity.image})` : undefined,
              opacity: index === currentHeroIndex ? 1 : 0,
            }}
            aria-hidden={index !== currentHeroIndex}
          />
        ))}

        {/* Overlay scuro per leggibilita */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60" />

        {/* Contenuto */}
        <div className="relative z-10 container mx-auto px-4 py-12 md:py-16">
          <div className="max-w-2xl mx-auto text-center">
            {/* Headline */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 tracking-tight drop-shadow-lg">
              Trova casa in
              <span className="text-blue-300"> un click</span>
            </h1>
            <p className="text-base md:text-lg text-gray-200 mb-8 max-w-md mx-auto drop-shadow">
              Annunci da Immobiliare.it, Idealista e altri portali in un unico posto.
            </p>

            {/* Search Box */}
            <div className="relative">
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-3 md:p-4 shadow-2xl">
                <div className="flex flex-col sm:flex-row gap-3">
                  {/* Input con autocomplete */}
                  <div className="flex-1 relative">
                    <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                    <Input
                      ref={inputRef}
                      placeholder="Dove vuoi cercare?"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      onFocus={() => city.length > 0 && setShowSuggestions(true)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          setShowSuggestions(false);
                          handleSearch();
                        }
                        if (e.key === 'Escape') {
                          setShowSuggestions(false);
                        }
                      }}
                      className="pl-12 h-14 text-base md:text-lg border-0 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-100 rounded-xl"
                      aria-label="Cerca citta o zona"
                      autoComplete="off"
                    />

                    {/* Autocomplete dropdown */}
                    {showSuggestions && (
                      <div
                        ref={suggestionsRef}
                        className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-50"
                      >
                        {filteredSuggestions.map((suggestion, index) => (
                          <button
                            key={suggestion}
                            onClick={() => handleSuggestionClick(suggestion)}
                            className={`w-full px-4 py-3 text-left hover:bg-gray-50 active:bg-gray-100 flex items-center gap-3 transition-colors ${
                              index !== filteredSuggestions.length - 1 ? 'border-b border-gray-50' : ''
                            }`}
                          >
                            <MapPin className="h-4 w-4 text-gray-400" />
                            <span className="font-medium text-gray-900">{suggestion}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Search button */}
                  <Button
                    size="lg"
                    className="h-14 px-8 rounded-xl text-base font-semibold shadow-md hover:shadow-lg transition-shadow"
                    onClick={() => handleSearch()}
                  >
                    <Search className="mr-2 h-5 w-5" />
                    Cerca
                  </Button>
                </div>
              </div>
            </div>

            {/* Quick links */}
            <div className="flex justify-center gap-4 mt-6">
              <button
                onClick={() => {
                  setFilters({ contractType: 'SALE' });
                  fetchListings();
                  navigate('/listings');
                }}
                className="text-sm text-white/80 hover:text-white transition-colors py-2 px-4 rounded-lg hover:bg-white/10"
              >
                In vendita
              </button>
              <span className="text-white/30">|</span>
              <button
                onClick={() => {
                  setFilters({ contractType: 'RENT' });
                  fetchListings();
                  navigate('/listings');
                }}
                className="text-sm text-white/80 hover:text-white transition-colors py-2 px-4 rounded-lg hover:bg-white/10"
              >
                In affitto
              </button>
            </div>

            {/* Indicatore citta corrente */}
            <div className="flex items-center justify-center gap-2 mt-6">
              {HERO_CITIES.map((heroCity, index) => (
                <button
                  key={heroCity.name}
                  onClick={() => setCurrentHeroIndex(index)}
                  className={`transition-all duration-300 ${
                    index === currentHeroIndex
                      ? 'w-8 h-2 rounded-full bg-white'
                      : 'w-2 h-2 rounded-full bg-white/40 hover:bg-white/60'
                  }`}
                  aria-label={heroCity.name}
                  title={heroCity.name}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Citta popolari - Touch-friendly cards */}
      <section className="py-8 md:py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg md:text-xl font-semibold text-gray-900">
              Citta popolari
            </h2>
            <button
              onClick={() => navigate('/listings')}
              className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1 py-2 px-3 rounded-lg hover:bg-blue-50 transition-colors"
            >
              Vedi tutte
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {/* Grid responsive con card touch-friendly */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
            {POPULAR_CITIES.map((cityData) => (
              <button
                key={cityData.name}
                onClick={() => handleCityClick(cityData.name)}
                className="group relative overflow-hidden rounded-xl bg-gray-50 p-4 md:p-5 text-left transition-all hover:bg-gray-100 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 min-h-[88px]"
              >
                <span className="block text-base md:text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                  {cityData.name}
                </span>
                <span className="block text-xs text-gray-400 mt-1">
                  {cityData.count} annunci
                </span>
                <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-300 group-hover:text-blue-400 group-hover:translate-x-1 transition-all" />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Footer minimale */}
      <footer className="py-6 bg-gray-50 border-t border-gray-100 mt-auto">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <img src="/logo-icon.svg" alt="" className="h-6 w-6" />
              <span className="text-sm font-medium text-gray-600">HomeFinder</span>
            </div>
            <div className="flex gap-6 text-sm text-gray-500">
              <a href="/privacy" className="hover:text-gray-900 transition-colors py-1">
                Privacy
              </a>
              <a href="/terms" className="hover:text-gray-900 transition-colors py-1">
                Termini
              </a>
              <a href="/contact" className="hover:text-gray-900 transition-colors py-1">
                Contatti
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
